import type { SelectableNestedTableRowModel } from './bespokeTemplatesImport';
import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
  RogerDocFormTypeCatalogEntry,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';
import type { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  filterBespokeTemplatesForPin,
  schemaVersionLabel,
} from './formSchemaUtils';
import { sortFormTypeCatalogByPrecedence } from './formPrecedence';
import { collectSchemaVersionNamesForFormTypes } from './bespokeTemplatesExport';

export type SectionValidation = {
  sectionId: string;
  label: string;
  pass: boolean;
};
export type SchemaVersionValidation = {
  /** Stable row id (schema version name + optional version suffix). */
  schemaVersionKey: string;
  schemaVersionName: string;
  label: string;
  sections: SectionValidation[];
};

export type ValidationStatus =
  | 'loading'
  | 'pass'
  | 'fail'
  | 'missing'
  | 'error';

/**
 * Parent status from children. Worst wins:
 * loading > error > fail > missing > pass.
 * Empty children => error (nothing was validated — never green Pass).
 */
export function rollupValidationStatus(
  sections: { status: ValidationStatus }[]
): ValidationStatus {
  if (sections.length === 0) return 'error';
  if (sections.some(s => s.status === 'loading')) return 'loading';
  if (sections.some(s => s.status === 'error')) return 'error';
  if (sections.some(s => s.status === 'fail')) return 'fail';
  if (sections.some(s => s.status === 'missing')) return 'missing';
  return 'pass';
}

/** Fail if ANY section fails, or if there are no sections. EMR-191: any child fail → fail. */
export function rollupPass(sections: { pass: boolean }[]): boolean {
  return sections.length > 0 && sections.every(s => s.pass);
}

export function buildValidationRows(
  groups: SchemaVersionValidation[]
): SelectableNestedTableRowModel[] {
  return groups.map(group => ({
    id: group.schemaVersionKey,
    primaryLine: group.label,
    branchLeafKeys: group.sections.map(s => s.sectionId),
    children: group.sections.map(s => ({
      id: s.sectionId,
      primaryLine: s.label,
      branchLeafKeys: [s.sectionId],
    })),
  })) as SelectableNestedTableRowModel[];
}

export function validationGroupKey(
  formType: RogerDocFormType,
  version: FormSchemaVersion
): string {
  return `${formType}::${(version.name ?? '').trim()}::${version.version ?? ''}`;
}

/** Stable row id when a form type's schema-version list could not be loaded. */
export function versionLoadFailureGroupKey(formType: RogerDocFormType): string {
  return `${formType}::__version_list_load_failed__`;
}

/**
 * Placeholder validation rows for form types whose schema-version fetch failed.
 * Keeps the form type visible in the modal with error status instead of omitting it.
 */
export function buildVersionLoadFailureGroups(
  catalog: readonly RogerDocFormTypeCatalogEntry[],
  failedFormTypes: ReadonlySet<RogerDocFormType>
): SchemaVersionValidation[] {
  if (failedFormTypes.size === 0) return [];

  const groups: SchemaVersionValidation[] = [];
  for (const entry of sortFormTypeCatalogByPrecedence(catalog)) {
    const formType = entry.form_type as RogerDocFormType;
    if (!failedFormTypes.has(formType)) continue;
    groups.push(versionLoadFailureGroup(entry));
  }
  return groups;
}

function versionLoadFailureGroup(
  entry: RogerDocFormTypeCatalogEntry
): SchemaVersionValidation {
  const formType = entry.form_type as RogerDocFormType;
  const formLabel = entry.display_label?.trim() || formType;
  return {
    schemaVersionKey: versionLoadFailureGroupKey(formType),
    schemaVersionName: '',
    label: `${formLabel} — Could not load schema versions`,
    sections: [],
  };
}

export type ValidationSchemaJob = {
  formType: RogerDocFormType;
  visitType: VisitTypeId;
  version: FormSchemaVersion;
  schemaVersionName: string;
  groupKey: string;
  groupLabel: string;
};

/** Unique schema version names referenced by bespoke template rows. */
export function schemaVersionNamesInTemplates(
  templates: readonly BespokeTemplateResponse[]
): string[] {
  const names = new Set<string>();
  for (const template of templates) {
    const name = template.schema_version_name.trim();
    if (name) names.add(name);
  }
  return [...names];
}

/**
 * One validation job per (form type, schema version) that owns templates.
 * Disambiguates duplicate schema version names across form types using the
 * template's own `form_type` pin rather than name matching.
 */
export function buildValidationSchemaJobs(params: {
  templates: readonly BespokeTemplateResponse[];
  catalog: RogerDocFormTypeCatalogEntry[];
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
}): ValidationSchemaJob[] {
  const templateNames = new Set(
    schemaVersionNamesInTemplates(params.templates)
  );
  if (templateNames.size === 0) return [];

  const jobs: ValidationSchemaJob[] = [];
  const seenKeys = new Set<string>();

  for (const entry of sortFormTypeCatalogByPrecedence(params.catalog)) {
    const formType = entry.form_type as RogerDocFormType;
    const versions = params.versionsByFormType.get(formType) ?? [];
    for (const version of versions) {
      const schemaVersionName = (version.name ?? '').trim();
      if (!schemaVersionName || !templateNames.has(schemaVersionName)) {
        continue;
      }
      const hasTemplatesForForm = params.templates.some(
        t =>
          t.form_type === formType &&
          t.schema_version_name.trim() === schemaVersionName
      );
      if (!hasTemplatesForForm) continue;

      const groupKey = validationGroupKey(formType, version);
      if (seenKeys.has(groupKey)) continue;
      seenKeys.add(groupKey);

      const formLabel = entry.display_label?.trim() || formType;
      jobs.push({
        formType,
        visitType: entry.canonical_visit_type_id,
        version,
        schemaVersionName,
        groupKey,
        groupLabel: `${formLabel} — ${schemaVersionLabel(version)}`,
      });
    }
  }

  return jobs;
}

/**
 * Validation rows for successful jobs and version-load failures in a single
 * precedence order, so a failed form type keeps its slot instead of sinking
 * below every form type that loaded.
 */
export function buildOrderedValidationGroups(params: {
  catalog: readonly RogerDocFormTypeCatalogEntry[];
  jobs: readonly ValidationSchemaJob[];
  failedFormTypes: ReadonlySet<RogerDocFormType>;
}): SchemaVersionValidation[] {
  const jobsByFormType = new Map<RogerDocFormType, ValidationSchemaJob[]>();
  for (const job of params.jobs) {
    const existing = jobsByFormType.get(job.formType);
    if (existing) existing.push(job);
    else jobsByFormType.set(job.formType, [job]);
  }

  const groups: SchemaVersionValidation[] = [];
  for (const entry of sortFormTypeCatalogByPrecedence(params.catalog)) {
    const formType = entry.form_type as RogerDocFormType;
    for (const job of jobsByFormType.get(formType) ?? []) {
      groups.push({
        schemaVersionKey: job.groupKey,
        schemaVersionName: job.schemaVersionName,
        label: job.groupLabel,
        sections: [],
      });
    }
    if (params.failedFormTypes.has(formType)) {
      groups.push(versionLoadFailureGroup(entry));
    }
  }
  return groups;
}

/** Templates attributed to one validation job (form type + schema version pin). */
export function filterTemplatesForValidationJob(params: {
  job: ValidationSchemaJob;
  templates: readonly BespokeTemplateResponse[];
  versionsByFormType: Map<RogerDocFormType, FormSchemaVersion[]>;
  sectionIds: string[];
}): BespokeTemplateResponse[] {
  const allowedNames = collectSchemaVersionNamesForFormTypes({
    formTypes: [params.job.formType],
    versionsByFormType: params.versionsByFormType,
  });
  const schemaVersionName = params.job.schemaVersionName.trim();
  if (!allowedNames.has(schemaVersionName)) {
    return [];
  }

  return filterBespokeTemplatesForPin(params.templates, {
    form_type: params.job.formType,
    schema_version_name: schemaVersionName,
    form_schema_version: params.job.version.version ?? '',
  }).filter(
    template =>
      template.section_name != null &&
      params.sectionIds.includes(template.section_name)
  );
}
